import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLSourceElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {MediaQueryList | undefined} */
  #media;
  /** @type {MediaQueryList | undefined} */
  #motion;
  #loaded = false;
  #sourceFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#media = window.matchMedia(this.dataset.media || 'all');
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#loaded = this.refs.videoSources.every((source) => source.getAttribute('src') === source.dataset.videoSource);
    const video = this.refs.videoElement;
    video.addEventListener('playing', () => this.setAttribute('data-video-ready', ''), { signal });
    video.addEventListener('emptied', () => this.removeAttribute('data-video-ready'), { signal });
    video.addEventListener('error', () => this.removeAttribute('data-video-ready'), { signal });
    this.#media.addEventListener('change', this.#refreshSources, { signal });
    this.#motion.addEventListener('change', this.#schedule, { signal });
    if (this.dataset.sourceMedia) {
      window.matchMedia(this.dataset.sourceMedia).addEventListener('change', this.#refreshSources, { signal });
    }
    this.#schedule();
  }

  #refreshSources = () => {
    // Coalesce responsive query changes into one media reload.
    cancelAnimationFrame(this.#sourceFrame);
    this.#sourceFrame = requestAnimationFrame(() => {
      if (this.#loaded && this.#media?.matches && !this.#motion?.matches) this.refs.videoElement.load();
      this.#schedule();
    });
  };

  #schedule = () => {
    this.#observer?.disconnect();
    if (!this.#media?.matches || this.#motion?.matches) {
      this.refs.videoElement.pause();
      return;
    }

    if (this.dataset.loading === 'eager' || !('IntersectionObserver' in window)) {
      this.#activate();
      return;
    }

    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer?.disconnect();
      this.#activate();
    }, { rootMargin: '400px 0px' });
    this.#observer.observe(this);
  };

  #activate() {
    if (!this.isConnected || !this.#media?.matches || this.#motion?.matches) return;
    const { videoElement, videoSources } = this.refs;
    if (!this.#loaded) {
      for (const source of videoSources) {
        if (source.dataset.videoSource) source.setAttribute('src', source.dataset.videoSource);
      }
      this.#loaded = true;
      videoElement.load();
    }
    // Autoplay can be rejected by the browser; the Liquid poster stays visible.
    videoElement.play().catch(() => {});
  }

  #cleanup() {
    this.#listeners?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#sourceFrame);
    this.refs.videoElement?.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
